import React, { useState, useEffect } from 'react';
import {
  getProductTargets,
  updateOpenPeriod,
  closeAndReassignProductTarget,
} from '../services/adminService';

const ProductTargetPanel = ({ teams, tid, isSuperAdmin, setMessage, styles }) => {
  const maxMonth = `${new Date().getFullYear() + 1}-12`;
  const parseYM  = s => { const [y, m] = s.split('-').map(Number); return { year: y, month: m }; };
  const nextYM   = s => {
    const { year, month } = parseYM(s);
    return month === 12 ? `${year + 1}-01` : `${year}-${String(month + 1).padStart(2, '0')}`;
  };
  const prevYM   = s => {
    const { year, month } = parseYM(s);
    return month === 1 ? `${year - 1}-12` : `${year}-${String(month - 1).padStart(2, '0')}`;
  };
  const fmtDate  = s => (s ? `${s.slice(5, 7)}/${s.slice(0, 4)}` : '—');
  const fmtYM    = s => (s ? `${s.slice(5, 7)}/${s.slice(0, 4)}` : '');

  // ── Filters ──────────────────────────────────────────────────────────────
  const [filterTeamId,  setFilterTeamId]  = useState('');
  const [productSearch, setProductSearch] = useState('');

  // ── List ─────────────────────────────────────────────────────────────────
  const [targets,   setTargets]   = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [listError, setListError] = useState('');

  // ── Form A: Durumu Düzenle (in-place) ────────────────────────────────────
  const [editId,         setEditId]         = useState(null);
  const [editIsIncluded, setEditIsIncluded] = useState(true);
  const [editMultiplier, setEditMultiplier] = useState('');
  const [editSaving,     setEditSaving]     = useState(false);
  const [editError,      setEditError]      = useState('');

  // ── Form B: Yeni Durum (ileri tarihli) ───────────────────────────────────
  const [newId,         setNewId]         = useState(null);
  const [newMonth,      setNewMonth]      = useState('');
  const [newIsIncluded, setNewIsIncluded] = useState(true);
  const [newMultiplier, setNewMultiplier] = useState('');
  const [newSaving,     setNewSaving]     = useState(false);
  const [newError,      setNewError]      = useState('');

  // ── Load ──────────────────────────────────────────────────────────────────
  const sortTargets = data => [...data].sort((a, b) => {
    const pn = (a.productName || '').localeCompare(b.productName || '', 'tr');
    if (pn !== 0) return pn;
    const tn = (a.teamName || '').localeCompare(b.teamName || '', 'tr');
    if (tn !== 0) return tn;
    return (a.startDate || '').localeCompare(b.startDate || '');
  });

  const loadTargets = async (teamId, pSearch) => {
    if (isSuperAdmin && !tid) { setTargets([]); return; }
    setLoading(true); setListError('');
    try {
      const data = await getProductTargets(
        tid || undefined,
        (teamId !== undefined ? teamId : filterTeamId) || undefined,
        (pSearch !== undefined ? pSearch : productSearch) || undefined,
      );
      setTargets(sortTargets(data));
    } catch (err) {
      setListError(err?.response?.data?.message || 'Yükleme başarısız.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setTargets([]); setFilterTeamId(''); setProductSearch('');
    setEditId(null); setNewId(null);
    loadTargets('', '');
  }, [tid]); // eslint-disable-line react-hooks/exhaustive-deps -- tid değişiminde filtreler sıfırlanır

  // ── Form A handlers ───────────────────────────────────────────────────────
  const openEdit = (target) => {
    setEditId(target.id);
    setEditIsIncluded(target.isIncluded);
    setEditMultiplier(target.isIncluded ? String(target.targetMultiplier ?? '') : '1');
    setEditError('');
    setNewId(null);
  };
  const cancelEdit = () => {
    setEditId(null); setEditIsIncluded(true); setEditMultiplier(''); setEditError('');
  };
  const handleEditStatus = async (id) => {
    const mult = editIsIncluded ? parseFloat(editMultiplier) : null;
    if (editIsIncluded && (!mult || mult <= 0)) { setEditError('Çarpan sıfırdan büyük olmalı.'); return; }
    setEditSaving(true); setEditError('');
    try {
      await updateOpenPeriod(id, tid || undefined, editIsIncluded, mult);
      setMessage('Dönem güncellendi.');
      cancelEdit();
      loadTargets();
    } catch (err) {
      setEditError(err?.response?.data?.message || 'İşlem başarısız.');
    } finally {
      setEditSaving(false);
    }
  };

  // ── Form B handlers ───────────────────────────────────────────────────────
  const openNew = (target) => {
    setNewId(target.id);
    setNewMonth(nextYM(target.startDate.slice(0, 7)));
    setNewIsIncluded(target.isIncluded);
    setNewMultiplier(target.isIncluded ? String(target.targetMultiplier ?? '1') : '1');
    setNewError('');
    setEditId(null);
  };
  const cancelNew = () => {
    setNewId(null); setNewMonth(''); setNewIsIncluded(true); setNewMultiplier(''); setNewError('');
  };
  const handleNewPeriod = async (id) => {
    if (!newMonth) { setNewError('Başlangıç ayı seçilmeli.'); return; }
    const { year, month } = parseYM(newMonth);
    const mult = newIsIncluded ? parseFloat(newMultiplier) : null;
    if (newIsIncluded && (!mult || mult <= 0)) { setNewError('Çarpan sıfırdan büyük olmalı.'); return; }
    setNewSaving(true); setNewError('');
    try {
      await closeAndReassignProductTarget(id, tid || undefined, year, month, newIsIncluded, mult);
      setMessage('Yeni dönem açıldı.');
      cancelNew();
      loadTargets();
    } catch (err) {
      setNewError(err?.response?.data?.message || 'İşlem başarısız.');
    } finally {
      setNewSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
        <select style={{ ...styles.input, minWidth: '160px' }} value={filterTeamId} onChange={e => setFilterTeamId(e.target.value)}>
          <option value="">Tüm Ekipler</option>
          {teams.map(t => <option key={t.id} value={t.id}>{t.teamName}</option>)}
        </select>
        <input
          placeholder="Ürün ara..."
          value={productSearch}
          onChange={e => setProductSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && loadTargets()}
          style={{ ...styles.input, minWidth: '180px' }}
        />
        <button style={styles.primaryButton} onClick={() => loadTargets()}>Filtrele</button>
      </div>

      {loading   && <p style={{ color: '#64748b', padding: '8px 0' }}>Yükleniyor...</p>}
      {listError && <p style={{ color: '#dc2626', padding: '8px 0' }}>{listError}</p>}
      {!loading && !listError && targets.length === 0 && (
        <p style={{ color: '#94a3b8', fontSize: '13px', padding: '8px 0' }}>
          {(isSuperAdmin && !tid) ? 'Tenant seçin.' : 'Kayıt bulunamadı.'}
        </p>
      )}
      {targets.length > 0 && (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Ürün</th>
              <th style={styles.th}>Ekip</th>
              <th style={styles.th}>Başlangıç</th>
              <th style={styles.th}>Bitiş</th>
              <th style={styles.th}>Durum</th>
              <th style={styles.th}>Aktif</th>
              <th style={styles.th}>İşlem</th>
            </tr>
          </thead>
          <tbody>
            {targets.map(target => (
              <React.Fragment key={target.id}>
                <tr>
                  <td style={styles.td}>{target.productName}</td>
                  <td style={styles.td}><span style={styles.tenantBadge}>{target.teamName}</span></td>
                  <td style={styles.td}>{fmtDate(target.startDate)}</td>
                  <td style={styles.td}>{fmtDate(target.endDate)}</td>
                  <td style={styles.td}>
                    {target.isIncluded
                      ? <span style={{ color: '#16a34a', fontWeight: '600', fontSize: '12px' }}>Dahil ×{target.targetMultiplier}</span>
                      : <span style={{ color: '#94a3b8', fontSize: '12px' }}>Hedef Dışı</span>
                    }
                  </td>
                  <td style={styles.td}>
                    <span style={{ color: target.isActive ? '#16a34a' : '#94a3b8', fontWeight: '600', fontSize: '12px' }}>
                      {target.isActive ? '● Aktif' : '○ Kapalı'}
                    </span>
                  </td>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                    {!target.endDate && (
                      <>
                        <button style={{ ...styles.pageButton, marginRight: '4px' }}
                          onClick={() => openEdit(target)}>Durumu Düzenle</button>
                        <button style={styles.pageButton}
                          onClick={() => openNew(target)}>Yeni Durum</button>
                      </>
                    )}
                  </td>
                </tr>

                {/* Form A: Durumu Düzenle */}
                {editId === target.id && (
                  <tr>
                    <td colSpan={7} style={{ ...styles.td, backgroundColor: '#eff6ff', padding: '12px 16px', borderLeft: '3px solid #3b82f6' }}>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <div>
                          <label style={styles.label}>Durum</label>
                          <select style={styles.input} value={editIsIncluded ? 'included' : 'excluded'}
                            onChange={e => {
                              const inc = e.target.value === 'included';
                              setEditIsIncluded(inc);
                              if (inc && !editMultiplier) setEditMultiplier('1');
                            }}>
                            <option value="included">Hedefe Dahil</option>
                            <option value="excluded">Hedef Dışı</option>
                          </select>
                        </div>
                        {editIsIncluded && (
                          <div>
                            <label style={styles.label}>Çarpan</label>
                            <input type="number" step="0.01" min="0.01"
                              style={{ ...styles.input, minWidth: '80px', width: '80px' }}
                              value={editMultiplier}
                              onChange={e => setEditMultiplier(e.target.value)}
                              autoFocus
                            />
                          </div>
                        )}
                        <button style={styles.primaryButton}
                          disabled={editSaving || (editIsIncluded && (!editMultiplier || parseFloat(editMultiplier) <= 0))}
                          onClick={() => handleEditStatus(target.id)}>
                          {editSaving ? 'Kaydediliyor...' : 'Kaydet'}
                        </button>
                        <button style={styles.pageButton} onClick={cancelEdit}>İptal</button>
                      </div>
                      {editError && <p style={{ color: '#dc2626', margin: '8px 0 0 0', fontSize: '13px' }}>{editError}</p>}
                    </td>
                  </tr>
                )}

                {/* Form B: Yeni Durum */}
                {newId === target.id && (
                  <tr>
                    <td colSpan={7} style={{ ...styles.td, backgroundColor: '#fefce8', padding: '12px 16px', borderLeft: '3px solid #fbbf24' }}>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <div>
                          <label style={styles.label}>Yeni Başlangıç Ayı</label>
                          <input type="month" style={styles.input} value={newMonth}
                            min={nextYM(target.startDate.slice(0, 7))} max={maxMonth}
                            onChange={e => setNewMonth(e.target.value)}
                          />
                        </div>
                        {newMonth && (
                          <div style={{ alignSelf: 'flex-end', paddingBottom: '8px' }}>
                            <span style={{ fontSize: '12px', color: '#92400e' }}>
                              Mevcut dönem {fmtYM(prevYM(newMonth))} sonunda kapanır.
                            </span>
                          </div>
                        )}
                        <div>
                          <label style={styles.label}>Durum</label>
                          <select style={styles.input} value={newIsIncluded ? 'included' : 'excluded'}
                            onChange={e => {
                              const inc = e.target.value === 'included';
                              setNewIsIncluded(inc);
                              if (inc && !newMultiplier) setNewMultiplier('1');
                            }}>
                            <option value="included">Hedefe Dahil</option>
                            <option value="excluded">Hedef Dışı</option>
                          </select>
                        </div>
                        {newIsIncluded && (
                          <div>
                            <label style={styles.label}>Çarpan</label>
                            <input type="number" step="0.01" min="0.01"
                              style={{ ...styles.input, minWidth: '80px', width: '80px' }}
                              value={newMultiplier}
                              onChange={e => setNewMultiplier(e.target.value)}
                            />
                          </div>
                        )}
                        <button style={styles.primaryButton}
                          disabled={newSaving || !newMonth || (newIsIncluded && (!newMultiplier || parseFloat(newMultiplier) <= 0))}
                          onClick={() => handleNewPeriod(target.id)}>
                          {newSaving ? 'İşleniyor...' : 'Kaydet'}
                        </button>
                        <button style={styles.pageButton} onClick={cancelNew}>İptal</button>
                      </div>
                      {newError && <p style={{ color: '#dc2626', margin: '8px 0 0 0', fontSize: '13px' }}>{newError}</p>}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ProductTargetPanel;
